import React, { useState, useEffect } from 'react';

const ActivityForm = ({ activity, students, teachers, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    activity_name: '',
    activity_name_en: '',
    activity_description: '',
    activity_category: '',
    activity_date: '',
    start_time: '',
    end_time: '',
    duration_hours: 1,
    location: '',
    organizer: '',
    target_grades: [],
    selected_students: []
  });
  const [showStudentSelector, setShowStudentSelector] = useState(false);
  const [errors, setErrors] = useState({});

  const activityCategories = [
    { value: '學術', label: '📚 學術' },
    { value: '體育', label: '🏃 體育' },
    { value: '藝術', label: '🎨 藝術' },
    { value: '社會服務', label: '🤝 社會服務' },
    { value: '領袖訓練', label: '👑 領袖訓練' },
    { value: '考試', label: '📝 考試' },
    { value: '假期', label: '🎊 假期' },
    { value: '典禮', label: '🏛️ 典禮' },
    { value: '競賽', label: '🏆 競賽' }
  ];

  const gradeOptions = [
    { value: '1', label: '中一' },
    { value: '2', label: '中二' },
    { value: '3', label: '中三' },
    { value: '4', label: '中四' },
    { value: '5', label: '中五' },
    { value: '6', label: '中六' }
  ];

  useEffect(() => {
    if (activity) {
      setFormData({
        activity_name: activity.activity_name || '',
        activity_name_en: activity.activity_name_en || '',
        activity_description: activity.activity_description || '',
        activity_category: activity.activity_category || '',
        activity_date: activity.activity_date || '',
        start_time: activity.start_time || '',
        end_time: activity.end_time || '',
        duration_hours: activity.duration_hours || 1,
        location: activity.location || '',
        organizer: activity.organizer || '',
        target_grades: activity.target_grades || [],
        selected_students: activity.selected_students || []
      });
    } else {
      // Set default date to today
      const today = new Date().toISOString().split('T')[0];
      setFormData(prev => ({
        ...prev,
        activity_date: today
      }));
    }
  }, [activity]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleGradeChange = (grade, checked) => {
    setFormData(prev => ({
      ...prev,
      target_grades: checked 
        ? [...prev.target_grades, grade]
        : prev.target_grades.filter(g => g !== grade)
    }));
  };

  const handleStudentChange = (studentId, checked) => {
    setFormData(prev => ({
      ...prev,
      selected_students: checked 
        ? [...prev.selected_students, studentId]
        : prev.selected_students.filter(id => id !== studentId)
    }));
  };

  const calculateDuration = () => {
    if (formData.start_time && formData.end_time) {
      const start = new Date(`2000-01-01T${formData.start_time}`);
      const end = new Date(`2000-01-01T${formData.end_time}`);
      const diffMs = end - start;
      const diffHours = Math.round(diffMs / (1000 * 60 * 60) * 10) / 10;
      if (diffHours > 0) {
        setFormData(prev => ({
          ...prev,
          duration_hours: diffHours
        }));
      }
    }
  };

  useEffect(() => {
    calculateDuration();
  }, [formData.start_time, formData.end_time, calculateDuration]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.activity_name.trim()) {
      newErrors.activity_name = '活動名稱為必填項目';
    }

    if (!formData.activity_category) {
      newErrors.activity_category = '活動類別為必填項目';
    }

    if (!formData.activity_date) {
      newErrors.activity_date = '活動日期為必填項目';
    }

    if (!formData.start_time) {
      newErrors.start_time = '開始時間為必填項目';
    }

    if (!formData.end_time) {
      newErrors.end_time = '結束時間為必填項目';
    }

    if (formData.start_time && formData.end_time && formData.start_time >= formData.end_time) {
      newErrors.end_time = '結束時間必須晚於開始時間';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      const activityData = {
        ...formData,
        participant_count: formData.selected_students.length
      };
      onSave(activityData);
    }
  };

  const getSelectedStudentNames = () => {
    return formData.selected_students.map(studentId => {
      const student = students.find(s => s.id === studentId);
      return student ? student.student_name : 'Unknown';
    });
  };

  return (
    <div className="activity-form-overlay">
      <div className="activity-form">
        <div className="form-header">
          <h2>{activity ? '編輯活動' : '新增活動'}</h2>
          <button className="close-btn" onClick={onCancel}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="activity-form-content">
          {/* 基本資料區塊 */}
          <div className="form-section">
            <h3>基本資料</h3>
            
            <div className="form-group">
              <label className="required">活動日期</label>
              <input
                type="date"
                value={formData.activity_date}
                onChange={(e) => handleInputChange('activity_date', e.target.value)}
                className={errors.activity_date ? 'error' : ''}
              />
              {errors.activity_date && <span className="error-message">{errors.activity_date}</span>}
            </div>

            <div className="form-group">
              <label className="required">活動類別</label>
              <select
                value={formData.activity_category}
                onChange={(e) => handleInputChange('activity_category', e.target.value)}
                className={errors.activity_category ? 'error' : ''}
              >
                <option value="">請選擇活動類別</option>
                {activityCategories.map(category => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
              {errors.activity_category && <span className="error-message">{errors.activity_category}</span>}
            </div>

            <div className="form-group">
              <label className="required">活動名稱</label>
              <input
                type="text"
                value={formData.activity_name}
                onChange={(e) => handleInputChange('activity_name', e.target.value)}
                placeholder="請輸入中文活動名稱"
                className={errors.activity_name ? 'error' : ''}
              />
              {errors.activity_name && <span className="error-message">{errors.activity_name}</span>}
            </div>

            <div className="form-group">
              <label>英文名稱</label>
              <input
                type="text"
                value={formData.activity_name_en}
                onChange={(e) => handleInputChange('activity_name_en', e.target.value)}
                placeholder="請輸入英文活動名稱 (選填)"
              />
            </div>

            <div className="form-group">
              <label>活動描述</label>
              <textarea
                value={formData.activity_description}
                onChange={(e) => handleInputChange('activity_description', e.target.value)}
                placeholder="請輸入活動詳細描述 (選填)"
                rows={3}
              />
            </div>
          </div>

          {/* 時間及地點區塊 */}
          <div className="form-section">
            <h3>時間及地點</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label className="required">開始時間</label>
                <input
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => handleInputChange('start_time', e.target.value)}
                  className={errors.start_time ? 'error' : ''}
                />
                {errors.start_time && <span className="error-message">{errors.start_time}</span>}
              </div>

              <div className="form-group">
                <label className="required">結束時間</label>
                <input
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => handleInputChange('end_time', e.target.value)}
                  className={errors.end_time ? 'error' : ''}
                />
                {errors.end_time && <span className="error-message">{errors.end_time}</span>}
              </div>

              <div className="form-group">
                <label>時數</label>
                <input
                  type="number"
                  value={formData.duration_hours}
                  onChange={(e) => handleInputChange('duration_hours', parseFloat(e.target.value))}
                  min="0.5"
                  max="24"
                  step="0.5"
                />
              </div>
            </div>

            <div className="form-group">
              <label>地點</label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                placeholder="請輸入活動地點"
              />
            </div>

            <div className="form-group">
              <label>主辦機構</label>
              <input
                type="text"
                value={formData.organizer}
                onChange={(e) => handleInputChange('organizer', e.target.value)}
                placeholder="請輸入主辦或協辦單位"
              />
            </div>
          </div>

          {/* 參與學生區塊 */}
          <div className="form-section">
            <h3>參與學生</h3>
            
            <div className="form-group">
              <label>目標年級</label>
              <div className="checkbox-group">
                {gradeOptions.map(grade => (
                  <label key={grade.value} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.target_grades.includes(grade.value)}
                      onChange={(e) => handleGradeChange(grade.value, e.target.checked)}
                    />
                    {grade.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>選擇學生</label>
              <button
                type="button"
                className="select-students-btn"
                onClick={() => setShowStudentSelector(true)}
              >
                選擇學生 ({formData.selected_students.length}人已選)
              </button>
              
              {formData.selected_students.length > 0 && (
                <div className="selected-students">
                  <h4>已選學生:</h4>
                  <div className="selected-student-list">
                    {getSelectedStudentNames().map((name, index) => (
                      <span key={index} className="selected-student-tag">
                        {name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="cancel-btn" onClick={onCancel}>
              取消
            </button>
            <button type="submit" className="save-btn">
              {activity ? '更新活動' : '儲存活動'}
            </button>
          </div>
        </form>

        {/* 學生選擇器 */}
        {showStudentSelector && (
          <StudentSelector
            students={students}
            selectedStudents={formData.selected_students}
            onStudentChange={handleStudentChange}
            onClose={() => setShowStudentSelector(false)}
          />
        )}
      </div>
    </div>
  );
};

// Student Selector Component
const StudentSelector = ({ students, selectedStudents, onStudentChange, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredStudents, setFilteredStudents] = useState(students);

  useEffect(() => {
    if (searchTerm) {
      const filtered = students.filter(student =>
        student.student_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.student_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.class_id.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredStudents(filtered);
    } else {
      setFilteredStudents(students);
    }
  }, [searchTerm, students]);

  const handleSelectAll = () => {
    filteredStudents.forEach(student => {
      if (!selectedStudents.includes(student.id)) {
        onStudentChange(student.id, true);
      }
    });
  };

  const handleDeselectAll = () => {
    filteredStudents.forEach(student => {
      if (selectedStudents.includes(student.id)) {
        onStudentChange(student.id, false);
      }
    });
  };

  return (
    <div className="student-selector-overlay">
      <div className="student-selector">
        <div className="selector-header">
          <h3>選擇學生</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="selector-controls">
          <input
            type="text"
            placeholder="搜尋學生..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <div className="selector-actions">
            <button type="button" onClick={handleSelectAll}>全選</button>
            <button type="button" onClick={handleDeselectAll}>取消全選</button>
          </div>
        </div>

        <div className="student-list">
          {filteredStudents.map(student => (
            <label key={student.id} className="student-item">
              <input
                type="checkbox"
                checked={selectedStudents.includes(student.id)}
                onChange={(e) => onStudentChange(student.id, e.target.checked)}
              />
              <div className="student-info">
                <span className="student-name">{student.student_name}</span>
                <span className="student-id">學號: {student.student_id}</span>
                <span className="student-class">班別: {student.class_id}</span>
              </div>
            </label>
          ))}
        </div>

        <div className="selector-footer">
          <div className="selection-count">
            已選擇 {selectedStudents.length} 名學生
          </div>
          <button className="confirm-btn" onClick={onClose}>
            確認選擇
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActivityForm;
